<script setup lang="ts">
  defineOptions({
    // Multi-root component: use explicit props/imgClass instead of fallthrough attrs.
    inheritAttrs: false,
  })

  /**
   * OptimisedImage Component - Streamlined Image Rendering with Unified Dimensions
   *
   * KEY DIFFERENCE FROM SmartImage:
   * - width/height props control BOTH server optimization AND display size
   * - Simpler, more intuitive API
   * - Use this when you want consistent dimensions for optimization and display
   *
   * WHY WE USE NUXTIMAGE:
   *
   * NuxtImage provides several critical features that make it superior to regular <img> tags:
   *
   * 1. **Automatic Image Optimization**:
   *    - Automatically generates optimized image formats (WebP, AVIF) based on browser support
   *    - Reduces image file sizes by up to 80% without quality loss
   *    - Handles responsive images with automatic srcset generation
   *
   * 2. **Server-Side Caching**:
   *    - Caches optimized images on the server (via IPX) to avoid re-processing
   *    - Cached images stored in `.nuxt/cache` for instant serving
   *    - Works with CDN edge caching for global distribution
   *    - Significantly reduces server load and response times
   *    - Pre-generates images at build time for static deployments
   *
   * 3. **Built-in Lazy Loading**:
   *    - Native lazy loading with intersection observer
   *    - Automatic placeholder generation for better UX
   *    - Defers off-screen images to improve initial page load
   *
   * 4. **Responsive Images**:
   *    - Automatic srcset and sizes generation based on viewport
   *    - Serves optimal image size for each device (mobile, tablet, desktop)
   *    - Reduces bandwidth usage on smaller devices
   *
   * 5. **Performance Optimizations**:
   *    - Image preloading for critical images (LCP optimization)
   *    - Async decoding to prevent main thread blocking
   *    - Fetch priority control for above-the-fold images
   *
   * 6. **Modern Image Formats**:
   *    - WebP for ~30% smaller file sizes with same quality
   *    - AVIF support for even better compression
   *    - Automatic fallback to legacy formats (JPEG/PNG) for older browsers
   *
   * 7. **SEO & Accessibility**:
   *    - Proper alt text handling
   *    - Structured image data for search engines
   *    - Better Core Web Vitals scores (LCP, CLS)
   *
   * 8. **DRM Integration**:
   *    - Works seamlessly with our DRM image URLs via generateDRMImageUrl()
   *    - Applies optimizations on top of DRM protection
   *    - Maintains license-based access control
   *
   * ═══════════════════════════════════════════════════════════════════════════════
   * PROP USAGE GUIDE:
   * ═══════════════════════════════════════════════════════════════════════════════
   *
   * WIDTH & HEIGHT PROPS (STREAMLINED):
   *
   * The width and height props control BOTH:
   * 1. Server-side image optimization (URL generation)
   * 2. Display size (NuxtImg width/height attributes)
   *
   * Example:
   * <OptimisedImage
   *   :src="imageUrl"
   *   :width="800"      <!-- Requests 800px wide image AND displays at 800px -->
   *   :height="600"     <!-- Requests 600px tall image AND displays at 600px -->
   *   img-class="rounded-lg"  <!-- Additional styling only -->
   * />
   *
   * Key Points:
   * - width/height: Controls BOTH optimization AND display (streamlined!)
   * - imgClass: For styling only (rounded, shadow, etc.) - NOT for dimensions
   * - When useRawUrl=true: width/height are still applied to NuxtImg for display
   *
   * Common Patterns:
   * 1. Fixed size image (no gradient):
   *    <OptimisedImage :src="url" :width="400" :height="300" />
   *    <!-- Image displays at exactly 400x300px -->
   *
   * 2. Square image (no gradient):
   *    <OptimisedImage :src="url" :width="200" :height="200" img-class="rounded-full" />
   *    <!-- Image displays at 200x200px with rounded styling -->
   *
   * 3. With gradient background (auto adds w-full h-full):
   *    <OptimisedImage :src="url" :width="800" gradient-mode="auto" />
   *    <!-- Automatically adds w-full h-full to fill container, gradient fills letterbox -->
   *
   * 4. In collages/grids (force gradient):
   *    <OptimisedImage :src="url" :width="600" gradient-mode="always" />
   *    <!-- Automatically adds w-full h-full to fill grid cell -->
   *
   * ⚠️ IMPORTANT:
   * - When gradientMode is 'auto' or 'always' → w-full h-full automatically added
   * - When gradientMode is 'none' → image uses explicit width/height props for sizing
   * - imgClass is for additional styling only (rounded, shadow, etc.)
   *
   * ⚠️ SmartImage is DEPRECATED and will be deleted.
   * OptimisedImage is the ONLY supported image component.
   * All SmartImage instances MUST be migrated to OptimisedImage.
   */

  import { createElementId } from '@common/core-layer/utils'
  import {
    computed,
    defineAsyncComponent,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
    type ComponentPublicInstance,
    type PropType,
  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useImageAspectRatio, type UseImageAspectRatioOptions } from '../composables/useImageAspectRatio'
  import { generateDRMImageUrl } from '../utils/image.utils'
  import { isValidBlurhash } from '../utils/blurhash.utils'
  import { isValidGalleryLicenseId } from '../utils/media-helpers'
  import BlurhashCanvas from './BlurhashCanvas.vue'

  const MediaGalleryModal = defineAsyncComponent(() => import('./MediaGalleryModal.vue'))
  const ImageErrorState = defineAsyncComponent(() => import('./ImageErrorState.vue'))

  const props = defineProps({
    id: {
      type: String,
      default: createElementId('thumbnail', 'image', 'container'),
      /**
       * Unique identifier for the image container
       */
    },
    licenseId: {
      type: String,
      default: '',
      /**
       * DRM (Digital Rights Management) license ID for protected assets
       *
       * When provided, generates an optimized DRM URL for the image.
       * If licenseId looks like a URL, it will be used as src instead.
       *
       * Example: licenseId="abc123xyz"
       */
    },
    src: {
      type: String,
      default: '',
      /**
       * Image source URL
       *
       * Can be:
       * - Regular URL: "https://example.com/image.jpg"
       * - Blob URL: "blob:http://localhost:3000/abc-123"
       * - Relative path: "/images/logo.png"
       * - GCS URL: "gs://bucket/image.jpg"
       *
       * If licenseId is provided, this is used as the source identifier
       */
    },
    source: {
      type: String as PropType<'communities' | 'courses' | 'clientportal' | string>,
      default: 'communities',
      /**
       * App source identifier for DRM URL generation
       *
       * Specifies which app/domain the image belongs to.
       * Used to construct the correct DRM URL path.
       *
       * Options: 'communities' | 'courses' | 'clientportal'
       */
    },
    alt: {
      type: String,
      default: '',
      /**
       * Alternative text for the image (accessibility)
       *
       * Describes the image content for screen readers and when image fails to load.
       * Should always be provided for content images.
       *
       * Example: alt="User profile photo of John Doe"
       */
    },
    fallbackAspect: {
      type: String,
      default: 'aspect-square',
      /**
       * Fallback aspect ratio class when image hasn't loaded yet
       *
       * Used to reserve space and prevent layout shift during image load.
       * Accepts Tailwind aspect ratio classes.
       *
       * Common values:
       * - 'aspect-square' → 1:1 ratio
       * - 'aspect-video' → 16:9 ratio
       * - 'aspect-[4/3]' → 4:3 ratio
       * - 'aspect-[21/9]' → 21:9 ratio
       */
    },
    contain: {
      type: Boolean,
      default: false,
      /**
       * Whether to contain (fit) image within bounds or cover (fill) the space
       *
       * false (default): object-cover - fills container, may crop image
       * true: object-contain - fits entire image, may show letterboxing
       *
       * When true and gradientMode is 'auto', shows gradient background for letterbox areas
       */
    },
    imgClass: {
      type: String,
      default: '',
      /**
       * Additional CSS classes for styling the image (rounded, shadow, opacity, etc.)
       *
       * ⚠️ NOTE: Width/height classes (w-*, h-*, max-w-*, etc.) are automatically filtered out
       * to prevent conflicts with the component's dimension management.
       *
       * Use the width/height props for sizing, not imgClass.
       */
    },
    rounded: {
      type: String,
      default: '',
      /**
       * Border radius class for the image container
       *
       * Accepts any Tailwind rounded class:
       * - 'rounded' → 4px border radius
       * - 'rounded-lg' → 8px border radius
       * - 'rounded-full' → circular/pill shape
       * - 'rounded-2xl' → 16px border radius
       * - etc.
       *
       * Example: rounded="rounded-full" for avatars
       */
    },
    gradientMode: {
      type: String as PropType<'none' | 'auto' | 'always'>,
      default: 'auto',
      validator: (val: string) => ['none', 'auto', 'always'].includes(val),
      /**
       * Controls gradient background behavior for letterboxing
       *
       * 'none': No gradient background, image uses explicit width/height
       * 'auto' (default): Smart gradient - only shows when aspect ratio mismatches
       * 'always': Force gradient background (used in grids/collages for uniform sizing)
       *
       * When 'auto' or 'always', automatically adds w-full h-full to fill container
       */
    },
    maxWidth: {
      type: Number,
      default: undefined,
      /**
       * Maximum width constraint for the image container (in pixels)
       *
       * Prevents image from growing beyond this width.
       * Useful for responsive layouts with size limits.
       *
       * Example: :max-width="800" limits container to 800px wide
       */
    },
    maxHeight: {
      type: Number,
      default: undefined,
      /**
       * Maximum height constraint for the image container (in pixels)
       *
       * Prevents image from growing beyond this height.
       * Useful for cards or constrained layouts.
       *
       * Example: :max-height="600" limits container to 600px tall
       */
    },
    width: {
      type: Number,
      default: undefined,
      /**
       * Width in pixels - controls BOTH optimization AND display size
       *
       * This prop is used for:
       * 1. DRM URL generation (wdt parameter) for server-side optimization
       * 2. NuxtImg width attribute for display sizing
       *
       * Example: :width="800" requests an 800px wide image AND displays it at 800px
       */
    },
    height: {
      type: Number,
      default: undefined,
      /**
       * Height in pixels - controls BOTH optimization AND display size
       *
       * This prop is used for:
       * 1. DRM URL generation (hgt parameter) for server-side optimization
       * 2. NuxtImg height attribute for display sizing
       *
       * Example: :height="600" requests a 600px tall image AND displays it at 600px
       */
    },
    quality: {
      type: Number,
      default: 90,
      /**
       * Image quality for optimization (1-100)
       *
       * Higher values = better quality but larger file size
       * Lower values = smaller file size but reduced quality
       *
       * Default: 90 (recommended balance)
       * Use 100 for critical images (logos, hero banners)
       * Use 70-80 for thumbnails or background images
       */
    },
    options: {
      type: Object as PropType<UseImageAspectRatioOptions>,
      default: () => ({}),
      /**
       * Advanced options for aspect ratio detection
       *
       * Passed to useImageAspectRatio composable.
       * Options include:
       * - tolerance: Aspect ratio matching tolerance (default: 0.15)
       * - mapping: Custom aspect ratio to class mapping
       *
       * Rarely needed - defaults work for most cases
       */
    },
    allowFullScreenPreview: {
      type: Boolean,
      default: false,
      /**
       * Whether clicking the image opens a full-screen preview modal
       *
       * When enabled:
       * - Image becomes clickable with cursor-pointer
       * - Click opens MediaGalleryModal for full-screen viewing
       * - Keyboard accessible (role="button", tabindex, arrow keys)
       *
       * Set to false for decorative images or when click behavior is handled elsewhere
       */
    },
    useRawUrl: {
      type: Boolean,
      default: false,
      /**
       * Bypass DRM optimization and use the raw source URL
       *
       * When true:
       * - Skips generateDRMImageUrl()
       * - Uses src prop value directly
       * - No optimization, resizing, or format conversion
       *
       * Use for:
       * - External URLs that don't support optimization
       * - Already optimized images
       * - When you need the exact original file
       */
    },
    // NuxtImg / native img element props
    loading: {
      type: String as PropType<'lazy' | 'eager'>,
      default: 'lazy',
      /**
       * Image loading strategy
       *
       * 'lazy' (default): Load when near viewport (better performance)
       * 'eager': Load immediately (use for above-the-fold critical images)
       *
       * Tip: Use 'eager' for hero banners, logos, and LCP images
       */
    },
    decoding: {
      type: String as PropType<'async' | 'auto' | 'sync'>,
      default: 'async',
      /**
       * Image decoding strategy
       *
       * 'async' (default): Decode off main thread (best performance)
       * 'sync': Decode on main thread (may block rendering)
       * 'auto': Browser decides
       *
       * Recommended: Keep default 'async' for smooth rendering
       */
    },
    fetchpriority: {
      type: String as PropType<'high' | 'low' | 'auto'>,
      default: 'auto',
      /**
       * Resource loading priority hint
       *
       * 'high': Prioritize loading (use for LCP/hero images)
       * 'low': Deprioritize loading (use for below-fold images)
       * 'auto' (default): Browser decides
       *
       * Example: :fetchpriority="'high'" for hero banner
       */
    },
    // Additional NuxtImg props
    format: {
      type: String,
      default: 'webp',
      /**
       * Output image format for optimization
       *
       * 'webp' (default): Modern format, ~30% smaller than JPEG
       * 'avif': Newer format, even better compression
       * 'jpg'/'png': Legacy formats for compatibility
       *
       * Applied during DRM URL generation (not passed to NuxtImg)
       */
    },
    preload: {
      type: [Boolean, Object] as PropType<boolean | { fetchPriority: 'auto' | 'high' | 'low' }>,
      default: false,
      /**
       * Preload image resource hint
       *
       * Boolean mode:
       * - true: Adds <link rel="preload"> with default priority
       * - false: No preload
       *
       * Object mode for fine control:
       * - { fetchPriority: 'high' }: High priority preload
       * - { fetchPriority: 'low' }: Low priority preload
       *
       * Use for critical above-the-fold images to improve LCP
       * Example: :preload="{ fetchPriority: 'high' }" for hero banner
       */
    },
    placeholder: {
      type: [String, Boolean, Number, Array],
      default: undefined,
      /**
       * Blur-up placeholder while image loads
       *
       * Modes:
       * - true: Auto-generate low-quality placeholder
       * - Number: Custom placeholder size (e.g., 10)
       * - [width, height, quality]: Full control (e.g., [100, 50, 10])
       * - String: Custom placeholder image URL
       *
       * Creates smooth loading experience with blur effect
       */
    },
    sizes: {
      type: String,
      default: undefined,
      /**
       * Responsive image sizes for different viewports
       *
       * Generates srcset for optimal image selection per device.
       *
       * Format: "viewport:size viewport:size"
       * Example: sizes="sm:100vw md:50vw lg:400px"
       *
       * Use for responsive images that change size across breakpoints
       */
    },
    densities: {
      type: String,
      default: undefined,
      /**
       * Pixel density variants for retina displays
       *
       * Generates srcset for different pixel densities (1x, 2x, 3x).
       *
       * Example: densities="x1 x2" generates 1x and 2x versions
       *
       * Use for:
       * - Critical images that need retina sharpness (hero, product photos)
       * - Skip for: thumbnails, icons, background images (performance)
       *
       * Tip: Leave undefined for most images to save bandwidth
       */
    },
    blurhash: {
      type: String,
      default: '',
      /**
       * BlurHash string for instant placeholder rendering while the full image loads.
       *
       * Rendered as a CSS gradient during SSR (zero JS), then upgraded to a crisp canvas
       * decode after hydration. Zero network cost. Hidden once the main image loads.
       */
    },
  })

  const emit = defineEmits<{
    onError: [event: Event]
    onLoad: [event: Event]
  }>()

  const { t } = useI18n()
  const optimizedImageUrl = computed(() => {
    // Use raw URL directly without optimization
    if (props.useRawUrl) {
      return props.src
    }

    // Blob URLs (during upload) bypass DRM optimization
    if (props.src?.startsWith('blob:')) {
      return props.src
    }

    // Validate licenseId: if it looks like a URL/path, treat it as src instead.
    // Some data sources store URLs in the licenseId field.
    // required for backward compatibility with existing data sources
    const actualLicenseId = isValidGalleryLicenseId(props.licenseId) ? props.licenseId : undefined
    const actualSrc = actualLicenseId ? props.src : props.licenseId || props.src

    // Generate optimized DRM URL
    return generateDRMImageUrl({
      licenseId: actualLicenseId,
      src: actualSrc,
      source: props.source,
      width: props.width,
      height: props.height,
      quality: props.quality,
      format: (props.format as 'jpg' | 'png' | 'webp' | 'avif' | 'gif' | 'tiff' | 'heif' | 'jp2' | 'jxl') || 'webp',
      resize: props.contain ? 'fit' : 'fill',
    })
  })
  const { isLoaded, aspectClass, objectFitClass, measure, reset, error, needsGradientBackground } = useImageAspectRatio({
    preferContain: props.contain,
    useGradientBackground: props.gradientMode !== 'none', // Enable when auto or always
    ...(props.options ?? {}),
  })
  const isReady = computed(() => isLoaded.value || Boolean(error.value))
  const hasError = ref(false)
  const imageLoaded = ref(false)
  const backgroundImageLoaded = ref(false)
  const blurhashVisible = ref(true)
  const mainImageRef = ref<ComponentPublicInstance | HTMLImageElement | null>(null)
  const backgroundImageRef = ref<ComponentPublicInstance | HTMLImageElement | null>(null)
  const aspectMeasurementUrl = ref('')
  const aspectMeasurementPending = ref(false)
  let blurhashRemovalTimeout: ReturnType<typeof setTimeout> | undefined
  const effectiveBlurhash = computed(() => (isValidBlurhash(props.blurhash) ? props.blurhash : ''))
  const shouldDeferImageReveal = computed(() => Boolean(effectiveBlurhash.value))
  const isBackgroundRequirementResolved = computed(
    () =>
      props.gradientMode !== 'auto' ||
      (isReady.value && !aspectMeasurementPending.value && aspectMeasurementUrl.value === optimizedImageUrl.value)
  )
  const shouldShowBackgroundImage = computed(() =>
    Boolean(
      optimizedImageUrl.value &&
        (props.gradientMode === 'always' ||
          (props.gradientMode === 'auto' && isBackgroundRequirementResolved.value && needsGradientBackground.value))
    )
  )
  const shouldRenderBackgroundImage = computed(() => imageLoaded.value && shouldShowBackgroundImage.value)
  const isImageRevealReady = computed(() => !shouldDeferImageReveal.value || (imageLoaded.value && isBackgroundRequirementResolved.value))
  const showBlurhash = computed(() => Boolean(effectiveBlurhash.value) && blurhashVisible.value && !hasError.value)
  const resolvedAspect = computed(() => {
    // When forcing gradient (in grids/collages), don't apply any aspect ratio
    if (props.gradientMode === 'always') {
      return ''
    }

    // When gradient background is enabled and we want object-contain,
    // use the fallback aspect to maintain a fixed container size
    // This creates the empty space that needs the gradient fill
    const shouldUseFallback = props.gradientMode === 'auto' && props.contain === false && isReady.value
    const aspect = shouldUseFallback
      ? props.fallbackAspect || 'aspect-video'
      : isReady.value
        ? aspectClass.value
        : props.fallbackAspect || 'aspect-square'

    return aspect
  })
  const fitClass = computed(() => objectFitClass.value)
  const containerClasses = computed(() => {
    const classes = ['relative overflow-hidden']

    // Add h-full w-full only when gradient is enabled (needs to fill parent container)
    // For regular images without gradient, let the image dimensions control the size
    if (props.gradientMode !== 'none') {
      classes.push('h-full w-full')
    }

    // Add aspect ratio class
    if (resolvedAspect.value) {
      classes.push(resolvedAspect.value)
    }

    // Add rounded class if provided
    if (props.rounded) {
      classes.push(props.rounded)
    }

    // Add cursor pointer if full-screen preview is enabled
    if (props.allowFullScreenPreview) {
      classes.push('cursor-pointer')
    }

    return classes.join(' ')
  })
  const imageClasses = computed(() => {
    const classes = ['relative z-10 w-full h-full', fitClass.value]

    // Add w-full h-full when gradient background is enabled (needs to fill container)
    // if (props.gradientMode !== 'none') {
    //   classes.push('w-full h-full')
    // }

    // Add user's custom classes, but filter out dimension classes to prevent conflicts
    if (props.imgClass) {
      const filtered = props.imgClass
        .split(' ')
        .filter(cls => {
          // Filter out width/height related classes
          return !(
            cls.startsWith('w-') ||
            cls.startsWith('h-') ||
            cls.startsWith('max-w-') ||
            cls.startsWith('max-h-') ||
            cls.startsWith('min-w-') ||
            cls.startsWith('min-h-') ||
            cls.startsWith('width-') ||
            cls.startsWith('height-')
          )
        })
        .join(' ')

      if (filtered) {
        classes.push(filtered)
      }
    }

    return classes.join(' ')
  })
  const mainImageClasses = computed(() => {
    const classes = [imageClasses.value]

    if (shouldDeferImageReveal.value) {
      classes.push('image-reveal-transition')
      if (!isImageRevealReady.value) {
        classes.push('image-reveal-hidden')
      }
    }

    return classes.join(' ')
  })
  const blurhashClasses = computed(() => [
    'blurhash-placeholder',
    'image-reveal-transition',
    isImageRevealReady.value ? 'blurhash-reveal-hidden' : 'blurhash-reveal-visible',
  ])
  const containerStyle = computed(() => {
    const style: Record<string, string> = {}

    // NOTE: width/height props control BOTH URL generation AND NuxtImg sizing
    // They are passed directly to NuxtImg component for display

    // Apply max dimensions only if explicitly provided
    if (props.maxWidth !== undefined) {
      style.maxWidth = `${props.maxWidth}px`
    }
    if (props.maxHeight !== undefined) {
      style.maxHeight = `${props.maxHeight}px`
    }

    if (props.gradientMode === 'none') {
      if (props.width !== undefined) {
        style.width = `${props.width}px`
      }
      if (props.height !== undefined) {
        style.height = `${props.height}px`
      }
    }

    return style
  })
  const showGallery = ref(false)
  const galleryAttachments = computed(() => {
    const validLicenseId = isValidGalleryLicenseId(props.licenseId) ? props.licenseId : undefined
    return [
      {
        type: 'image' as const,
        url: optimizedImageUrl.value,
        id: validLicenseId || props.src,
        licenseId: validLicenseId,
        alt: props.alt,
      },
    ]
  })
  const handleImageClick = () => {
    if (props.allowFullScreenPreview) {
      showGallery.value = true
    }
  }
  const closeGallery = () => {
    showGallery.value = false
  }
  const handleKeyDown = (event: KeyboardEvent) => {
    if (props.allowFullScreenPreview && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      handleImageClick()
    }
  }

  const getImageElement = (target: ComponentPublicInstance | HTMLImageElement | null): HTMLImageElement | null => {
    if (typeof HTMLImageElement === 'undefined') return null

    if (!target) return null
    if (target instanceof HTMLImageElement) return target

    const root = target.$el
    if (root instanceof HTMLImageElement) return root
    if (root instanceof HTMLElement) return root.querySelector('img')

    return null
  }

  const getMainImageElement = (): HTMLImageElement | null => getImageElement(mainImageRef.value)
  const getBackgroundImageElement = (): HTMLImageElement | null => getImageElement(backgroundImageRef.value)

  const measureCurrentImageAspect = async () => {
    const url = optimizedImageUrl.value
    if (!url) return

    aspectMeasurementUrl.value = url
    aspectMeasurementPending.value = true
    await measure(url)
    if (optimizedImageUrl.value === url) {
      aspectMeasurementPending.value = false
    }
  }

  const ensureAspectMeasuredAfterImageLoad = () => {
    if (aspectMeasurementUrl.value !== optimizedImageUrl.value && !aspectMeasurementPending.value) {
      void measureCurrentImageAspect()
    }
  }

  const clearBlurhashRemovalTimeout = () => {
    if (blurhashRemovalTimeout) {
      clearTimeout(blurhashRemovalTimeout)
      blurhashRemovalTimeout = undefined
    }
  }

  const scheduleBlurhashRemoval = () => {
    if (!showBlurhash.value || !isImageRevealReady.value) return

    clearBlurhashRemovalTimeout()
    blurhashRemovalTimeout = setTimeout(() => {
      if (isImageRevealReady.value) {
        blurhashVisible.value = false
      }
      blurhashRemovalTimeout = undefined
    }, 500)
  }

  const syncAlreadyLoadedImage = async () => {
    if (!shouldDeferImageReveal.value) return

    await nextTick()

    const image = getMainImageElement()
    if (image?.complete && image.naturalWidth > 0) {
      imageLoaded.value = true
      hasError.value = false
      ensureAspectMeasuredAfterImageLoad()
      scheduleBlurhashRemoval()
    }
  }

  const syncAlreadyLoadedBackgroundImage = async () => {
    if (!shouldRenderBackgroundImage.value) return

    await nextTick()

    const image = getBackgroundImageElement()
    if (image?.complete && image.naturalWidth > 0) {
      backgroundImageLoaded.value = true
      scheduleBlurhashRemoval()
    }
  }

  /** Guards onLoad against double-emitting when both the cache sync and a real load event fire. */
  const loadEmitted = ref(false)

  // A cached image can already be `complete` before Vue attaches the @load listener, so the browser
  // never fires `load` and consumers waiting on the emitted `onLoad` (e.g. image skeletons) hang.
  // syncAlreadyLoadedImage only covers blurhash reveal and never emits; this emits onLoad for any
  // already-complete image (regardless of blurhash), guarded so it fires at most once per URL.
  const emitLoadIfAlreadyComplete = async () => {
    await nextTick()
    const image = getMainImageElement()
    if (image?.complete && image.naturalWidth > 0) {
      onLoad(new Event('load'))
    }
  }

  onMounted(() => {
    if (props.loading === 'eager') {
      void measureCurrentImageAspect()
    }
    void syncAlreadyLoadedImage()
    void syncAlreadyLoadedBackgroundImage()
    void emitLoadIfAlreadyComplete()
  })
  onBeforeUnmount(clearBlurhashRemovalTimeout)

  function onLoad(event: Event) {
    imageLoaded.value = true
    ensureAspectMeasuredAfterImageLoad()
    hasError.value = false
    scheduleBlurhashRemoval()
    if (loadEmitted.value) return
    loadEmitted.value = true
    emit('onLoad', event)
  }
  function onBlurhashTransitionEnd(event: TransitionEvent) {
    if (event.propertyName === 'opacity' && isImageRevealReady.value) {
      clearBlurhashRemovalTimeout()
      blurhashVisible.value = false
    }
  }
  function onBackgroundLoad() {
    backgroundImageLoaded.value = true
    scheduleBlurhashRemoval()
  }
  function onBackgroundError() {
    // Do not keep the blurhash forever if the decorative letterbox image fails.
    backgroundImageLoaded.value = true
    scheduleBlurhashRemoval()
  }
  function onError(event: Event | string) {
    hasError.value = true
    // NuxtImg can emit either an Event or a string error message
    // Normalize to Event for consistent consumer API
    const errorEvent = typeof event === 'string' ? new Event('error') : event
    emit('onError', errorEvent)
  }
  watch(
    () => optimizedImageUrl.value,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl) {
        hasError.value = false
        imageLoaded.value = false
        backgroundImageLoaded.value = false
        aspectMeasurementUrl.value = ''
        aspectMeasurementPending.value = false
        blurhashVisible.value = true
        loadEmitted.value = false
        clearBlurhashRemovalTimeout()
        reset()
        if (newUrl && props.loading === 'eager') {
          void measureCurrentImageAspect()
        }
        // Cached images can complete before a load event reaches Vue, so confirm
        // the new DOM image state once Vue swaps the keyed NuxtImg.
        void syncAlreadyLoadedImage()
        void syncAlreadyLoadedBackgroundImage()
        void emitLoadIfAlreadyComplete()
      }
    }
  )
  watch(
    () => shouldRenderBackgroundImage.value,
    shouldRender => {
      backgroundImageLoaded.value = false
      if (shouldRender) {
        void syncAlreadyLoadedBackgroundImage()
      }
    }
  )
  watch(
    () => isImageRevealReady.value,
    revealReady => {
      if (revealReady) {
        scheduleBlurhashRemoval()
      }
    }
  )
</script>

<template>
  <!--
    Accessibility: This div acts as an interactive button when allowFullScreenPreview is true.
    We dynamically bind role="button", tabindex="0", and aria-label via v-bind.
    The click/keydown handlers are guarded internally (only fire when allowFullScreenPreview is true).
    ESLint can't statically detect the dynamic role binding, so we suppress the warning here.
  -->
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <div
    :id="id"
    :class="containerClasses"
    :style="containerStyle"
    v-bind="
      allowFullScreenPreview
        ? {
            role: 'button',
            tabindex: 0,
            'aria-label': t('media.clickToPreview'),
          }
        : {}
    "
    @click="handleImageClick"
    @keydown="handleKeyDown"
  >
    <!-- BlurHash placeholder: CSS gradient during SSR, canvas after hydration (zero network cost) -->
    <BlurhashCanvas
      v-if="showBlurhash"
      :id="createElementId('thumbnail', 'image', 'blurhash')"
      :class="blurhashClasses"
      :hash="effectiveBlurhash"
      :width="32"
      :height="32"
      @transitionend="onBlurhashTransitionEnd"
    />

    <!-- Blurred background image for empty spaces -->
    <NuxtImg
      v-if="shouldRenderBackgroundImage"
      :id="createElementId('thumbnail', 'image', 'background')"
      ref="backgroundImageRef"
      :key="`bg-${optimizedImageUrl}`"
      :src="optimizedImageUrl"
      :alt="`Background for ${alt || 'image'}`"
      loading="eager"
      decoding="async"
      class="absolute inset-0 h-full w-full scale-110 object-cover opacity-100 blur-md"
      @load="onBackgroundLoad"
      @error="onBackgroundError"
    />

    <!-- Main image with streamlined width/height handling -->
    <NuxtImg
      v-if="optimizedImageUrl && !hasError"
      :id="createElementId('thumbnail', 'image', 'main')"
      ref="mainImageRef"
      :key="optimizedImageUrl"
      :src="optimizedImageUrl"
      :alt="alt || ''"
      :loading="loading"
      :decoding="decoding"
      :fetchpriority="fetchpriority"
      :preload="preload"
      :placeholder="placeholder"
      :sizes="sizes"
      :densities="densities"
      :class="mainImageClasses"
      @load="onLoad"
      @error="onError"
    />

    <!-- Error state - lazy loaded, only when image fails -->
    <ImageErrorState v-if="hasError" />

    <!-- Loading shimmer overlay -->
    <!-- <Transition name="fade">
      <div
        v-if="isLoading"
        class="absolute inset-0 shimmer-bg z-30"
      />
    </Transition> -->
  </div>

  <!-- Media Gallery Modal -->
  <MediaGalleryModal
    :show="showGallery"
    :attachments="galleryAttachments"
    :initial-index="0"
    :show-thumbnails="false"
    :allow-navigation="false"
    :use-raw-url="useRawUrl"
    @close="closeGallery"
  />
</template>

<style scoped>
  .blurhash-placeholder {
    position: absolute;
    inset: 0;
    z-index: 5;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-reveal-transition {
    transition: opacity 0.5s ease-out;
  }

  .image-reveal-hidden,
  .blurhash-reveal-hidden {
    opacity: 0;
  }

  .blurhash-reveal-visible {
    opacity: 1;
  }

  /* Shimmer animation */
  .shimmer-bg {
    background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  /* Fade transition */
  .fade-enter-active {
    transition: opacity 0.4s ease-in;
  }

  .fade-leave-active {
    transition: opacity 0.3s ease-out;
  }

  .fade-enter-from {
    opacity: 0;
  }

  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }
</style>
